import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ErrorRetry } from '@bechtel/client-common';
import { GET_COMMODITY_STATS_SUMMARY } from '@bechtel/client-gql';
import { Box, Icon, IconSize, Menu, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import {
  CommodityFilterByTerm,
  CommodityFilterInput,
  CommodityMilestoneStatusStats,
  CommodityStatsSummary,
  FilterOperator,
  MilestoneStatusCode,
  ProjectMilestoneType,
} from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { find, get, map } from 'lodash';
import { FC, Fragment, useEffect } from 'react';
import { FitupStatsSummaryMessage, InstallStatsSummaryMessage } from '~/components/atoms/i18n-message-keys';
import { useCommodity } from '~/detail/commodities/context';
import { MilestoneName } from '../types';
import { fitupMilestoneStats, installMilestoneStats } from './form-data';
import { PopperContainerStyled, StatBoxStyled } from './styled';
interface WeldStatsProps {
  label: string;
  milestoneName: string;
  projectMilestoneTypes: ProjectMilestoneType[];
}
const WeldStats: FC<WeldStatsProps> = ({ label, milestoneName, projectMilestoneTypes }) => {
  const { i18n } = useLingui();
  const { id } = useCommodity();

  const [getCommodityStatsSummary, { loading, data, error, refetch, networkStatus }] = useLazyQuery<
    { statusStats: CommodityStatsSummary },
    {
      input: CommodityFilterInput;
    }
  >(GET_COMMODITY_STATS_SUMMARY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  useEffect(() => {
    if (
      !get(
        find(projectMilestoneTypes, (projectMilestoneType) => projectMilestoneType.name === milestoneName),
        'id'
      )
    )
      return;
    const commodityFilterInput: CommodityFilterInput = {
      complexFilter: {
        and: [
          {
            operator: FilterOperator.EQUALS,
            term: CommodityFilterByTerm.CONNECTED_COMMODITY_ID,
            value: id,
          },
          {
            operator: FilterOperator.EQUALS,
            term: CommodityFilterByTerm.PROJECT_MILESTONE_TYPE_ID,
            value: get(
              find(projectMilestoneTypes, (projectMilestoneType) => projectMilestoneType.name === milestoneName),
              'id'
            ),
          },
        ],
      },
    };
    getCommodityStatsSummary({
      variables: {
        input: commodityFilterInput,
      },
    });
  }, [milestoneName, id]);

  const statusStats: CommodityMilestoneStatusStats[] = get(data, 'commodityStatsSummary.statusStats', []);
  const totalWelds = get(data, 'commodityStatsSummary.numCommodities', 0);
  const weldStats = milestoneName === MilestoneName.Fitup ? fitupMilestoneStats : installMilestoneStats;
  const stats = map(weldStats, (statsItem) => {
    statsItem.value =
      get(
        find(statusStats, (statusStat) => statusStat.status === statsItem.status),
        'stats'
      ) || 0;

    return statsItem;
  });

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>
              There was a problem loading commodity stats summary. Please click the button below to try again.
            </Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <StatBoxStyled data-testid="welds-stats-container">
          <Box data-testid="weld-menu" flexGrow={{ sm: 1 }}>
            <Menu>
              {({ isOpen, toggle, close: closeMenu }) => (
                <div
                  style={{ position: 'relative', width: '100%' }}
                  onBlur={() => {
                    closeMenu();
                  }}
                >
                  <Box
                    data-testid="weld-selected-label"
                    justifyContent={{ sm: 'space-between' }}
                    onClick={() => toggle()}
                    css={{ cursor: 'pointer' }}
                  >
                    <Box flexDirection={{ sm: 'column' }}>
                      <Text size="300" color="grey.600">
                        {i18n._(label)}
                      </Text>
                      <Box mt={{ sm: '200' }} data-testid="weld-approved-stats">
                        <Text size="400" weight="600" color="grey.1000">
                          {find(stats, (stat) => stat.status === MilestoneStatusCode.APPROVED)?.value ?? 0}/{totalWelds}{' '}
                          <Trans>Approved</Trans>
                        </Text>
                      </Box>
                    </Box>
                    <Icon fillColor="grey.600" size={IconSize.MEDIUM} icon="circle-info" />
                  </Box>

                  <PopperContainerStyled data-testid="weld-stats-list">
                    {isOpen && (
                      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }} pb={{ sm: '400' }}>
                        <Box
                          px={{ sm: '400' }}
                          py={{ sm: '500' }}
                          css={`
                            border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                          `}
                        >
                          <Text weight={600} size={400}>
                            {i18n._(label)} <Trans> Summary</Trans>
                          </Text>
                        </Box>
                        <Box
                          justifyContent={{ sm: 'space-between' }}
                          mt={{ sm: '300' }}
                          px={{ sm: '400' }}
                          py={{ sm: '300' }}
                        >
                          <Text weight={340} size={400} color="grey.800">
                            <Trans>Total Welds</Trans>
                          </Text>
                          <Text weight={600} size={400} color="grey.800">
                            {totalWelds}
                          </Text>
                        </Box>
                        {stats.length > 0 &&
                          map(stats, (stat) => (
                            <Box
                              justifyContent={{ sm: 'space-between' }}
                              mt={{ sm: '300' }}
                              px={{ sm: '400' }}
                              py={{ sm: '300' }}
                              key={stat.status}
                            >
                              <Text weight={340} size={400} color="grey.800">
                                {i18n._(
                                  milestoneName === MilestoneName.Fitup
                                    ? FitupStatsSummaryMessage[stat.status]
                                    : InstallStatsSummaryMessage[stat.status]
                                )}
                              </Text>
                              <Text weight={600} size={400} color="grey.800">
                                {stat.value}
                              </Text>
                            </Box>
                          ))}
                      </Box>
                    )}
                  </PopperContainerStyled>
                </div>
              )}
            </Menu>
          </Box>
        </StatBoxStyled>
      )}
    </Fragment>
  );
};

export default WeldStats;
